import HeroSection from '../components/HeroSection'
import StatCounter from '../components/StatCounter'
import { GraduationCap, ClipboardList, Sprout, Handshake, ArrowRight, Globe, Users } from 'lucide-react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'
import CarteProgram from '../components/CarteProgram'
import CarteProjet from '../components/CarteProjet'
import Partners from '../components/Partners'

const Home = () => {
  const domains = [
    {
      title: "Formation Professionnelle Continue",
      icon: <GraduationCap size={40} className="text-cidh-gold" />,
      desc: "Renforcement des capacités des Organisations professionnelles du secteur formel et informel (Entreprises privées et publiques, Ong, Coopératives, groupements,)"
    },
    {
      title: "Etudes",
      icon: <ClipboardList size={40} className="text-cidh-gold" />,
      desc: "Etudes de références, Etudes d'impact, Evaluation de projets, Etudes diagnostiques, Plans d'affaires, etc."
    },
    {
      title: "Appui au développement communautaire et rural",
      icon: <Sprout size={40} className="text-cidh-gold" />,
      desc: "Formulation, mise en œuvre et suivi de projets de développement. Suivi/Encadrement des ex-combattants, des jeunes à risques, des populations rurales dans la réalisation d'activités génératrices de revenus, Conseil agricole, Structuration des investissements agricoles, Développement des chaines de valeur, Appui organisationnel des OPA, etc."
    },
    {
      title: "Conseils",
      icon: <Handshake size={40} className="text-cidh-gold" />,
      desc: "Création d'entreprises selon la méthodologie GERME (TRIE, CRE, GERME), Management des Organisations Professionnelles Agricoles, ONG et OCB, etc."
    }
  ]

  return (
    <div>
      <HeroSection />
      
      <StatCounter />

      {/* Nos Domaines */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl mb-4 text-cidh-blue">Nos Domaines d'Expertise</h2>
            <div className="w-20 h-1 bg-cidh-gold mx-auto mb-6"></div>
            <p className="text-cidh-gray-text">Une approche pluridisciplinaire pour répondre aux enjeux de développement en Afrique.</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {domains.map((domain, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ y: -10 }}
                className="bg-cidh-gray-light p-8 rounded-xl border border-gray-100 shadow-sm hover:shadow-xl transition-all"
              >
                <div className="mb-6">{domain.icon}</div>
                <h3 className="text-xl font-bold mb-4 text-cidh-blue font-montserrat">{domain.title}</h3>
                <p className="text-cidh-gray-text text-lg leading-relaxed">{domain.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Zones d'intervention & Effectif */}
      <section className="section-padding bg-cidh-blue text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-cidh-gold/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-cidh-green/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Zones d'interventions */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-cidh-gold rounded-xl flex items-center justify-center">
                  <Globe size={28} className="text-white" />
                </div>
                <h2 className="text-3xl font-bold font-montserrat">Zones d'intervention</h2>
              </div>
              
              <div className="space-y-6">
                {[
                  { level: "National", desc: "Le Centre est organisé pour pouvoir intervenir en tout lieu, en tout temps sur toute l'étendue du territoire ivoirien" },
                  { level: "Régional", desc: "Le Centre est organisé pour pouvoir intervenir en tout lieu, en tout temps sur toute l'étendue des régions." },
                  { level: "Local", desc: "Le Centre est organisé pour pouvoir intervenir en tout lieu, en tout temps au niveau local." }
                ].map((item, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm">
                    <h3 className="text-cidh-gold font-bold text-xl mb-2">{item.level}</h3>
                    <p className="text-gray-300 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Effectif */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white text-cidh-blue p-8 md:p-12 rounded-3xl shadow-2xl"
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-cidh-blue rounded-xl flex items-center justify-center">
                  <Users size={28} className="text-white" />
                </div>
                <h2 className="text-3xl font-bold font-montserrat text-cidh-blue">Notre Effectif</h2>
              </div>
              
              <div className="space-y-6 text-cidh-gray-text text-lg leading-relaxed">
                <p>
                  Dirigé par une équipe rompue aux techniques de renforcement de capacité et de développement communautaire, le Centre dispose de plus de <span className="font-bold text-cidh-blue text-2xl">70 formateurs</span>, experts seniors et juniors tous titulaires de Doctorat (Docteurs-ingénieurs, 3è cycle, d’Etat, Ph. D.), de diplômes d’ingénieurs et de Maîtrise, de BTS, jouissant d’une expertise avérée en matière d’appui au développement.
                </p>
                <div className="h-px bg-gray-100 w-full my-6"></div>
                <p>
                  Parmi ceux-ci, <span className="font-bold text-cidh-gold text-2xl">40</span> interviennent de manière permanente au Cabinet dans le cadre de l’élaboration, le suivi de projets et le renforcement des capacités dans des entreprises et structures de développement communautaire (Collectivités, Mutuelles de développement, Conseils régionaux, Organismes internationaux, …).
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="section-padding bg-cidh-gray-light">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl md:text-5xl font-extrabold mb-4 text-cidh-blue font-montserrat uppercase tracking-tight">Projets Réalisés</h2>
            <div className="w-24 h-1.5 bg-cidh-gold mx-auto mb-6 rounded-full"></div>
            <p className="text-cidh-gray-text text-lg">Aperçu de nos interventions majeures pour le développement durable.</p>
          </div>
          
          <FeaturedContent />

          <div className="flex justify-center mt-16">
            <Link 
              to="/projets" 
              className="px-10 py-4 bg-cidh-blue text-white font-bold rounded-xl hover:bg-cidh-blue/90 shadow-lg hover:shadow-2xl transition-all flex items-center gap-3 translate-y-0 hover:-translate-y-1"
            >
              Voir plus de projets <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Partenaires */}
      <Partners />
    </div>
  )
}

const FeaturedContent = () => {
  const { data: projets, isLoading } = useQuery({
    queryKey: ['featured-projets-home'],
    queryFn: async () => {
      const res = await api.get('/projets/accueil')
      return res.data
    }
  })

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white p-4 rounded-xl shadow-sm animate-pulse h-96"></div>
        ))}
      </div>
    )
  }

  const items = (projets || []).slice(0, 3)

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        Aucun projet à afficher pour le moment.
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {items.map((projet) => (
        <CarteProjet key={projet.id} projet={projet} />
      ))}
    </div>
  )
}

export default Home
